import {
  type NotificationRow,
  type NotificationSurface,
} from "@nexys/user-management-client";
import { AlertCircle, Trash2 } from "lucide-react";
import { useState, type FormEvent } from "react";

import { useUmClient } from "../../context/auth-context.js";
import { useCall } from "../../hooks/use-call.js";
import { errorMessage } from "../../lib/error-message.js";
import { Alert, AlertDescription } from "../ui/alert.js";
import { Badge } from "../ui/badge.js";
import { Button } from "../ui/button.js";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card.js";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog.js";
import { Field } from "../ui/field.js";
import { Input } from "../ui/input.js";
import { Label } from "../ui/label.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select.js";
import { Skeleton } from "../ui/skeleton.js";
import { Switch } from "../ui/switch.js";
import { Textarea } from "../ui/textarea.js";

export interface ProjectNoticesProps {
  projectId: string;
  canManage?: boolean;
}

const SURFACES: { value: NotificationSurface; label: string }[] = [
  { value: "any", label: "Everywhere" },
  { value: "login", label: "Login screen" },
  { value: "signup", label: "Sign-up screen" },
  { value: "app", label: "Inside the app" },
];

const SEVERITIES = ["info", "warning", "critical"] as const;
type Severity = (typeof SEVERITIES)[number];

/** `datetime-local` wants `YYYY-MM-DDTHH:mm` in local time. */
const toLocalInput = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

/** Back to RFC 3339 for the API; "" means "no bound". */
const toIso = (local: string): string =>
  local.trim() === "" ? "" : new Date(local).toISOString();

/** Why a notice isn't showing right now — the thing you want to see at a glance. */
const liveState = (n: NotificationRow): { label: string; live: boolean } => {
  if (!n.enabled) return { label: "disabled", live: false };
  const now = Date.now();
  if (n.startsAt && new Date(n.startsAt).getTime() > now) {
    return { label: "scheduled", live: false };
  }
  if (n.endsAt && new Date(n.endsAt).getTime() <= now) {
    return { label: "expired", live: false };
  }
  return { label: "live", live: true };
};

export const ProjectNotices = ({
  projectId,
  canManage = false,
}: ProjectNoticesProps) => {
  const { data, loading, error, refresh } = useCall("listNotifications", {
    projectId,
  });
  const notices = data?.notifications ?? [];

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle />
        <AlertDescription>{errorMessage(error)}</AlertDescription>
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notices</CardTitle>
        <CardDescription>
          Messages shown to this product's users. A notice appears while it is
          enabled and inside its date window; dismissals are remembered per
          user.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {loading ? (
          <Skeleton className="h-20 w-full" />
        ) : notices.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No notices yet.
          </p>
        ) : (
          <div className="grid gap-2">
            {notices.map((n) => (
              <NoticeRow
                key={n.id}
                projectId={projectId}
                notice={n}
                canManage={canManage}
                onChanged={refresh}
              />
            ))}
          </div>
        )}
        {canManage ? (
          <NoticeDialog projectId={projectId} onSaved={refresh} />
        ) : null}
      </CardContent>
    </Card>
  );
};

const NoticeRow = ({
  projectId,
  notice,
  canManage,
  onChanged,
}: {
  projectId: string;
  notice: NotificationRow;
  canManage: boolean;
  onChanged: () => void;
}) => {
  const client = useUmClient();
  const [busy, setBusy] = useState(false);
  const state = liveState(notice);

  const toggleEnabled = async () => {
    setBusy(true);
    await client.updateNotification({
      projectId,
      notificationId: notice.id,
      enabled: !notice.enabled,
    });
    setBusy(false);
    onChanged();
  };

  const remove = async () => {
    setBusy(true);
    await client.deleteNotification({
      projectId,
      notificationId: notice.id,
    });
    setBusy(false);
    onChanged();
  };

  const window = [
    notice.startsAt ? `from ${new Date(notice.startsAt).toLocaleString()}` : null,
    notice.endsAt ? `until ${new Date(notice.endsAt).toLocaleString()}` : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border bg-card p-3">
      <div className="min-w-0 grid gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">{notice.title}</span>
          <Badge variant={state.live ? "default" : "muted"}>{state.label}</Badge>
          <Badge variant="muted">
            {SURFACES.find((s) => s.value === notice.surface)?.label ??
              notice.surface}
          </Badge>
          {notice.severity !== "info" ? (
            <Badge variant="muted">{notice.severity}</Badge>
          ) : null}
          {!notice.dismissible ? (
            <Badge variant="muted">not dismissible</Badge>
          ) : null}
        </div>
        <p className="text-muted-foreground truncate text-xs">{notice.body}</p>
        {window ? (
          <p className="text-muted-foreground text-xs">{window}</p>
        ) : null}
      </div>
      {canManage ? (
        <div className="flex shrink-0 items-center gap-2">
          <NoticeDialog
            projectId={projectId}
            notice={notice}
            onSaved={onChanged}
          />
          <Switch
            checked={notice.enabled}
            onCheckedChange={() => void toggleEnabled()}
            disabled={busy}
            aria-label="Enabled"
          />
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            onClick={() => void remove()}
            disabled={busy}
            aria-label="Delete notice"
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      ) : null}
    </div>
  );
};

/** Create when `notice` is absent, edit when present. */
const NoticeDialog = ({
  projectId,
  notice,
  onSaved,
}: {
  projectId: string;
  notice?: NotificationRow;
  onSaved: () => void;
}) => {
  const client = useUmClient();
  const editing = notice !== undefined;
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(notice?.title ?? "");
  const [body, setBody] = useState(notice?.body ?? "");
  const [surface, setSurface] = useState<NotificationSurface>(
    (notice?.surface as NotificationSurface | undefined) ?? "any"
  );
  const [severity, setSeverity] = useState<(typeof SEVERITIES)[number]>(
    (notice?.severity as Severity | undefined) ?? "info"
  );
  const [startsAt, setStartsAt] = useState(toLocalInput(notice?.startsAt ?? null));
  const [endsAt, setEndsAt] = useState(toLocalInput(notice?.endsAt ?? null));
  const [dismissible, setDismissible] = useState(notice?.dismissible ?? true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const windowValid =
    !startsAt || !endsAt || new Date(endsAt) > new Date(startsAt);

  const reset = () => {
    setTitle(notice?.title ?? "");
    setBody(notice?.body ?? "");
    setSurface((notice?.surface as NotificationSurface | undefined) ?? "any");
    setSeverity((notice?.severity as Severity | undefined) ?? "info");
    setStartsAt(toLocalInput(notice?.startsAt ?? null));
    setEndsAt(toLocalInput(notice?.endsAt ?? null));
    setDismissible(notice?.dismissible ?? true);
    setError(null);
    setLoading(false);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const shared = {
      title: title.trim(),
      body,
      surface,
      severity: severity as "info" | "warning" | "critical",
      dismissible,
    };
    const res = editing
      ? await client.updateNotification({
          projectId,
          notificationId: notice.id,
          ...shared,
          // "" clears a bound server-side; omitting would keep it.
          startsAt: toIso(startsAt),
          endsAt: toIso(endsAt),
        })
      : await client.createNotification({
          projectId,
          ...shared,
          startsAt: toIso(startsAt) || null,
          endsAt: toIso(endsAt) || null,
        });
    setLoading(false);
    if (res.ok) {
      onSaved();
      setOpen(false);
      if (!editing) reset();
    } else {
      setError(errorMessage(res.error, "Couldn't save this notice."));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger asChild>
        {editing ? (
          <Button variant="ghost" size="sm">
            Edit
          </Button>
        ) : (
          <Button variant="outline" size="sm">
            New notice
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit notice" : "New notice"}</DialogTitle>
          <DialogDescription>
            Leave a date empty for no bound on that side.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4" noValidate>
          <Field label="Title" htmlFor="notice-title" required>
            <Input
              id="notice-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Field>
          <Field label="Message" htmlFor="notice-body" required>
            <Textarea
              id="notice-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Where" htmlFor="notice-surface">
              <Select
                value={surface}
                onValueChange={(v) => setSurface(v as NotificationSurface)}
              >
                <SelectTrigger id="notice-surface">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SURFACES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Severity" htmlFor="notice-severity">
              <Select
                value={severity}
                onValueChange={(v) =>
                  setSeverity(v as (typeof SEVERITIES)[number])
                }
              >
                <SelectTrigger id="notice-severity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEVERITIES.map((s) => (
                    <SelectItem key={s} value={s} className="capitalize">
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Starts" htmlFor="notice-starts">
              <Input
                id="notice-starts"
                type="datetime-local"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
              />
            </Field>
            <Field label="Ends" htmlFor="notice-ends">
              <Input
                id="notice-ends"
                type="datetime-local"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                aria-invalid={!windowValid}
              />
            </Field>
          </div>
          {!windowValid ? (
            <p className="text-destructive text-xs">
              The end must come after the start.
            </p>
          ) : null}
          <div className="flex items-center justify-between gap-4">
            <div className="grid gap-0.5">
              <Label htmlFor="notice-dismissible">Dismissible</Label>
              <p className="text-muted-foreground text-xs">
                Off keeps it visible for everyone while it's in window.
              </p>
            </div>
            <Switch
              id="notice-dismissible"
              checked={dismissible}
              onCheckedChange={setDismissible}
            />
          </div>
          {error ? (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              disabled={!title.trim() || !body.trim() || !windowValid}
            >
              {editing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
